import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Divider,
    Stack,
    Tooltip,
    IconButton,
    InputAdornment,
    Collapse,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Restore as RestoreIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Security as SecurityIcon,
    Mail as MailIcon,
    Speed as PerformanceIcon,
    Settings as SystemIcon,
    ConfirmationNumber as LicenseIcon,
    Storage as StorageIcon,
    Visibility,
    VisibilityOff,
    LocationOn as LocationIcon,
    VpnKey as SsoIcon
} from '@mui/icons-material';
import { blobticService } from '../services/blobticService';
import { blobticMessage } from '../components/BlobticMessage';
import { useLanguage } from '../contexts/LanguageContext';
import { BlobticDataManager } from '../services/blobticDataManager';
import GenericPage from '../components/GenericPage';

// Interface pour un paramètre
interface SysParameter {
    Code: string;
    Value: string;
    Type: string;
    Libelle: string;
    LastUpdate?: string;
    UserId: number;
    UserLib?: string;
}

// Groupes de paramètres
const PARAM_GROUPS = {
    MESSAGING: ['String_Envoi_Mail'],
    SSO: [
        'SSO_ENABLED',
        'SSO_DEFAULT_PROVIDER',
        'SSO_GOOGLE_ENABLED',
        'SSO_GOOGLE_CLIENT_ID',
        'SSO_GOOGLE_CLIENT_SECRET',
        'SSO_MICROSOFT_ENABLED',
        'SSO_MICROSOFT_CLIENT_ID',
        'SSO_MICROSOFT_CLIENT_SECRET',
        'SSO_MICROSOFT_TENANT_ID',
        'SSO_OIDC_ENABLED',
        'SSO_OIDC_NAME',
        'SSO_OIDC_AUTHORITY',
        'SSO_OIDC_CLIENT_ID',
        'SSO_OIDC_CLIENT_SECRET'
    ],
    SECURITY: ['WEB_SECURITY_ENABLED', 'Session_Expiration_Minutes', 'JWT_Expiration_Minutes', 'Password_Min_Length', 'Double_Authentication_Enabled', 'Double_Auth_Method', 'OTP_Expiration_Minutes', 'OTP_Max_Attempts', 'Geo_Restriction_Enabled', 'Geo_Restriction_Mode', 'Geo_Restriction_Countries'],
    PERFORMANCE: ['IDXDB_CACHE_ENABLED', 'Sync_Notifications_Timer_Seconds', 'ObjectPool_MaxSize', 'DictionaryPool_MaxSize', 'ListPool_MaxSize', 'DataManager_Cache_Duration_Minutes', 'Compression_Threshold_Bytes', 'Response_Cache_Duration_Seconds', 'Pool_Hit_Rate_Threshold', 'Cache_Hit_Rate_Threshold', 'Max_Creation_Time_Ms'],
    LICENSING: ['Licensed_Applications', 'License_Max_Users', 'License_Max_Dossiers', 'License_Storage_Limit_GB', 'License_Monitoring_Interval_Minutes'],
    MAINTENANCE: ['Auto_Cleanup_Expired_OTP_Hours', 'Auto_Cleanup_Expired_Sessions_Hours', 'Log_Retention_Days', 'Performance_Metrics_Retention_Hours'],
    SYSTEM: ['Default_App_Name', 'Currency_Symbol', 'Alert_Cooldown_Minutes', 'LoadTest_Iterations', 'LoadTest_Operations_Per_Iteration', 'LoadTest_Batch_Size', 'LoadTest_Batch_Delay_Ms'],
    GPS: ['GPS_Provider', 'GPS_API_Key', 'GPS_Endpoint_URL']
};

export const SystemParametersPage: React.FC = () => {
    // Options definitions
    const PARAM_OPTIONS: Record<string, { value: string; labelKey: string }[]> = {
        'SSO_ENABLED': [
            { value: '0', labelKey: 'page.sys_parameters.option.disabled' },
            { value: '1', labelKey: 'page.sys_parameters.option.enabled' }
        ],
        'SSO_DEFAULT_PROVIDER': [
            { value: 'google', labelKey: 'Google' },
            { value: 'microsoft', labelKey: 'Microsoft' },
            { value: 'oidc', labelKey: 'OpenID Connect' }
        ],
        'SSO_GOOGLE_ENABLED': [
            { value: '0', labelKey: 'page.sys_parameters.option.disabled' },
            { value: '1', labelKey: 'page.sys_parameters.option.enabled' }
        ],
        'SSO_MICROSOFT_ENABLED': [
            { value: '0', labelKey: 'page.sys_parameters.option.disabled' },
            { value: '1', labelKey: 'page.sys_parameters.option.enabled' }
        ],
        'SSO_OIDC_ENABLED': [
            { value: '0', labelKey: 'page.sys_parameters.option.disabled' },
            { value: '1', labelKey: 'page.sys_parameters.option.enabled' }
        ],
        'Double_Authentication_Enabled': [
            { value: '0', labelKey: 'page.sys_parameters.option.disabled' },
            { value: '1', labelKey: 'page.sys_parameters.option.enabled' }
        ],
        'WEB_SECURITY_ENABLED': [
            { value: '0', labelKey: 'page.sys_parameters.option.disabled' },
            { value: '1', labelKey: 'page.sys_parameters.option.enabled' }
        ],
        'IDXDB_CACHE_ENABLED': [
            { value: '0', labelKey: 'page.sys_parameters.option.disabled' },
            { value: '1', labelKey: 'page.sys_parameters.option.enabled' }
        ],
        'Geo_Restriction_Enabled': [
            { value: '0', labelKey: 'page.sys_parameters.option.disabled' },
            { value: '1', labelKey: 'page.sys_parameters.option.enabled' }
        ],
        'Geo_Restriction_Mode': [
            { value: 'ALLOW_ONLY', labelKey: 'page.sys_parameters.option.geo.allow_only' },
            { value: 'BLOCK_ONLY', labelKey: 'page.sys_parameters.option.geo.block_only' }
        ],
        'Double_Auth_Method': [
            { value: 'OTP', labelKey: 'page.sys_parameters.option.method.otp' },
            { value: 'SMS', labelKey: 'page.sys_parameters.option.method.sms' },
            { value: 'EMAIL', labelKey: 'page.sys_parameters.option.method.email' }
        ],
        'GPS_Provider': [
            { value: 'GoogleMaps', labelKey: 'Google Maps' },
            { value: 'Mapbox', labelKey: 'Mapbox' },
            { value: 'OpenStreetMap', labelKey: 'OpenStreetMap' }
        ]
    };

    
    const { t } = useLanguage();

    const [parameters, setParameters] = useState<SysParameter[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
        MESSAGING: true,
        SSO: true,
        SECURITY: true,
        PERFORMANCE: false,
        LICENSING: false,
        MAINTENANCE: false,
        SYSTEM: false,
        GPS: false,
        OTHER: true
    });

    // Etats locaux pour les champs en cours d'édition
    const [editValues, setEditValues] = useState<Record<string, string>>({});
    // Etats locaux pour les champs SMTP (car c'est un seul paramètre éclaté)
    const [smtpValues, setSmtpValues] = useState<Record<string, string>>({});
    const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({}); // Track visibility

    const togglePasswordVisibility = (key: string) => {
        setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Chargement des paramètres
    const fetchParameters = async () => {
        try {
            setIsLoading(true);
            const response = await blobticService.executeZoomRequest<SysParameter[]>({
                ZoomKey: 'sys_parameters',
                Action: {
                    OKey: 'Blobtic_Parameters,BlobticParameters,get_all'
                }
            });

            if (response.success && response.data) {
                setParameters(response.data);

                // Initialiser les valeurs locales
                const newEditValues: Record<string, string> = {};
                response.data.forEach(p => {
                    newEditValues[p.Code] = p.Value;
                    if (p.Code === 'String_Envoi_Mail') {
                        parseSmtpString(p.Value);
                    }
                });
                setEditValues(newEditValues);
            } else {
                blobticMessage.error(t('page.parameters.error.load'), response.message || '');
            }
        } catch (err: any) {
            console.error('Erreur chargement paramètres:', err);
            blobticMessage.error(t('page.parameters.error.load'), err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchParameters();
    }, []);

    // Helper pour parser la chaine SMTP
    const parseSmtpString = (smtpString: string) => {
        const parts = smtpString.split(';');
        const values: Record<string, string> = {};
        parts.forEach(part => {
            const [key, val] = part.split('=');
            if (key) values[key.trim()] = val ? val.trim() : '';
        });
        setSmtpValues(values);
    };

    // Helper pour reconstruire la chaine SMTP
    const buildSmtpString = () => {
        return Object.entries(smtpValues)
            .map(([key, val]) => `${key}=${val}`)
            .join(';');
    };

    const handleUpdate = async (code: string, value: string) => {
        try {
            const response = await blobticService.executeZoomRequest<any>({
                ZoomKey: 'sys_parameters_update',
                Action: {
                    OKey: 'Blobtic_Parameters,BlobticParameters,update',
                    SYSPAR_CODE: code,
                    SYSPAR_VALUE: value
                }
            });

            if (response.success) {
                blobticMessage.success(t('page.sys_parameters.success.update'), t('page.sys_parameters.success.message', code));

                if (code === 'IDXDB_CACHE_ENABLED') {
                    const isEnabled = value !== '0';
                    BlobticDataManager.setEnabled(isEnabled);
                    if (!isEnabled) {
                        BlobticDataManager.setContext('');
                    }
                }

                // Update local list to reflect last update time (optimistic update)
                let updatedMeta = response.data;
                if (Array.isArray(updatedMeta) && updatedMeta.length > 0) {
                    updatedMeta = updatedMeta[0];
                }

                setParameters(prev => prev.map(p => {
                    if (p.Code === code) {
                        return {
                            ...p,
                            Value: value,
                            LastUpdate: updatedMeta?.LastUpdate,
                            UserLib: updatedMeta?.UserLib
                        };
                    }
                    return p;
                }));
            } else {
                blobticMessage.error(t('page.parameters.error.update'), response.message || '');
            }
        } catch (err: any) {
            blobticMessage.error(t('page.parameters.error.update'), err.message);
        }
    };

    const handleSmtpUpdate = () => {
        const fullString = buildSmtpString();
        handleUpdate('String_Envoi_Mail', fullString);
    };

    const handleReset = async () => {
        if (!window.confirm(t('page.sys_parameters.confirm_reset'))) return;

        try {
            setIsLoading(true);
            const response = await blobticService.executeZoomRequest<any>({
                ZoomKey: 'sys_parameters_reset',
                Action: {
                    OKey: 'Blobtic_Parameters,BlobticParameters,reset_defaults'
                }
            });

            if (response.success) {
                blobticMessage.success(t('page.sys_parameters.success.reset'), t('page.sys_parameters.success.reset_detail') || t('page.sys_parameters.success.reset'));
                fetchParameters();
            } else {
                blobticMessage.error(t('page.sys_parameters.error.reset'), response.message || '');
            }
        } catch (err: any) {
            blobticMessage.error(t('page.sys_parameters.error.reset'), err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleGroup = (group: string) => {
        setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
    };

    const renderUser = (param: SysParameter) => {
        if (param.UserLib) return param.UserLib;
        // Safeguard against backend returning {} for DBNull which crashes React
        if (typeof param.UserId === 'object') return '';
        return param.UserId;
    };

    const renderParameterInput = (param: SysParameter) => {
        const isSmtp = param.Code === 'String_Envoi_Mail';
        if (isSmtp) {
            // Render SMTP specially
            return (
                <Grid container spacing={2}>
                    {Object.entries(smtpValues).map(([key, val]) => {
                        const isBooleanSmtp = ['SMTP_Server', 'SMPT_UseExplicitTLS', 'SMTP_SSL', 'SMTP_AcceptAllCerts'].includes(key);
                        return (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                                {isBooleanSmtp ? (
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        SelectProps={{
                                            native: true,
                                        }}
                                        label={key.replace('SMTP_', '')}
                                        value={val}
                                        onChange={(e) => setSmtpValues(prev => ({ ...prev, [key]: e.target.value }))}
                                        variant="outlined"
                                    >
                                        {PARAM_OPTIONS['Double_Authentication_Enabled'].map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {t(option.labelKey)}
                                            </option>
                                        ))}
                                    </TextField>
                                ) : key === 'SMTP_Password' ? (
                                    <TextField
                                        label={key.replace('SMTP_', '')}
                                        value={val}
                                        onChange={(e) => setSmtpValues(prev => ({ ...prev, [key]: e.target.value }))}
                                        size="small"
                                        fullWidth
                                        type={showPasswords[key] ? 'text' : 'password'}
                                        variant="outlined"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => togglePasswordVisibility(key)}
                                                        edge="end"
                                                    >
                                                        {showPasswords[key] ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                ) : (
                                    <TextField
                                        label={key.replace('SMTP_', '')}
                                        value={val}
                                        onChange={(e) => setSmtpValues(prev => ({ ...prev, [key]: e.target.value }))}
                                        size="small"
                                        fullWidth
                                        type={key === 'SMTP_Port' ? 'number' : 'text'}
                                        variant="outlined"
                                    />
                                )}
                            </Grid>
                        );
                    })}
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSmtpUpdate}
                            sx={{ mt: 1 }}
                        >
                            {t('action.save')}
                        </Button>
                        {param.LastUpdate && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                {t('page.sys_parameters.meta.info', new Date(param.LastUpdate).toLocaleString(), renderUser(param))}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            );
        }

        return (
            <Stack direction="row" spacing={2} alignItems="flex-start">
                {PARAM_OPTIONS[param.Code] ? (
                    <Box sx={{ flexGrow: 1 }}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            SelectProps={{
                                native: true,
                            }}
                            value={editValues[param.Code] || ''}
                            onChange={(e) => setEditValues(prev => ({ ...prev, [param.Code]: e.target.value }))}
                            label={param.Code}
                            helperText={t(`page.sys_parameters.lib.${param.Code}`) || param.Libelle}
                        >
                            {PARAM_OPTIONS[param.Code].map((option) => (
                                <option key={option.value} value={option.value}>
                                    {t(option.labelKey)}
                                </option>
                            ))}
                        </TextField>
                        {param.LastUpdate && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                {t('page.sys_parameters.meta.info', new Date(param.LastUpdate).toLocaleString(), renderUser(param))}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ flexGrow: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type={
                                (param.Code.includes('SECRET') || param.Code === 'GPS_API_Key')
                                    ? (showPasswords[param.Code] ? 'text' : 'password')
                                    : (param.Type === 'N' ? 'number' : 'text')
                            }
                            value={editValues[param.Code] || ''}
                            onChange={(e) => setEditValues(prev => ({ ...prev, [param.Code]: e.target.value }))}
                            label={param.Code}
                            helperText={t(`page.sys_parameters.lib.${param.Code}`) || param.Libelle}
                            InputProps={
                                (param.Code.includes('SECRET') || param.Code === 'GPS_API_Key')
                                    ? {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => togglePasswordVisibility(param.Code)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPasswords[param.Code] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                    : undefined
                            }
                        />
                        {param.LastUpdate && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                {t('page.sys_parameters.meta.info', new Date(param.LastUpdate).toLocaleString(), renderUser(param))}
                            </Typography>
                        )}
                    </Box>
                )}
                <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<SaveIcon />}
                    onClick={() => handleUpdate(param.Code, editValues[param.Code])}
                >
                    {t('action.update')}
                </Button>
            </Stack>
        );
    };

    const getGroupIcon = (groupName: string) => {
        switch (groupName) {
            case 'MESSAGING': return <MailIcon />;
            case 'SSO': return <SsoIcon />;
            case 'SECURITY': return <SecurityIcon />;
            case 'PERFORMANCE': return <PerformanceIcon />;
            case 'LICENSING': return <LicenseIcon />;
            case 'MAINTENANCE': return <StorageIcon />;
            case 'GPS': return <LocationIcon />;
            default: return <SystemIcon />;
        }
    };

    const renderGroup = (groupName: string, paramCodes: string[]) => {
        const groupParams = parameters.filter(p => paramCodes.some(c => c.trim().toLowerCase() === (p.Code || '').trim().toLowerCase()));
        if (groupParams.length === 0) return null;

        return (
            <Card key={groupName} sx={{ mb: 3 }}>
                <CardHeader
                    avatar={getGroupIcon(groupName)}
                    title={
                        <Typography variant="h6">
                            {t(`page.sys_parameters.group.${groupName.toLowerCase()}`)}
                        </Typography>
                    }
                    action={
                        <IconButton onClick={(e) => { e.stopPropagation(); toggleGroup(groupName); }}>
                            {expandedGroups[groupName] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    }
                    sx={{ bgcolor: 'action.hover', cursor: 'pointer' }}
                    onClick={() => toggleGroup(groupName)}
                />
                <Collapse in={expandedGroups[groupName]}>
                    <CardContent>
                        <Stack spacing={3}>
                            {groupParams.map(param => (
                                <Box key={param.Code}>
                                    {renderParameterInput(param)}
                                    <Divider sx={{ mt: 2 }} />
                                </Box>
                            ))}
                        </Stack>
                    </CardContent>
                </Collapse>
            </Card>
        );
    };

    return (
        <GenericPage
            title={t('page.sys_parameters.title')}
            icon={<SystemIcon />}
            footerEnabled={false}
            additionalActions={
                <>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchParameters}
                    >
                        {t('action.refresh')}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<RestoreIcon />}
                        onClick={handleReset}
                        sx={{ ml: 1 }}
                    >
                        {t('page.sys_parameters.action.reset')}
                    </Button>
                </>
            }
        >
            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        {t('page.sys_parameters.subtitle')}
                    </Typography>
                    {Object.entries(PARAM_GROUPS).map(([group, codes]) => renderGroup(group, codes))}
                    {(() => {
                        const allCategorizedCodes = new Set(
                            Object.values(PARAM_GROUPS).flat().map(c => c.trim().toLowerCase())
                        );
                        const otherCodes = parameters
                            .filter(p => !allCategorizedCodes.has((p.Code || '').trim().toLowerCase()))
                            .map(p => p.Code);
                        return otherCodes.length > 0 ? renderGroup('OTHER', otherCodes) : null;
                    })()}
                </Box>
            )}
        </GenericPage>
    );
};

export default SystemParametersPage;
